import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { toast } from 'react-toastify'
import MainLoader from '@/components/Common/MainLoader'
import getIsUserTeams from '@/lib/admin/getIsUserTeams'
import { getRolesUserAndSwitchUser } from '@/lib/auth/getRolesUserAndSwitchUser'

import { queryClient } from '@/lib/hook/tanstackQuery'
import { setEntity, useEntity } from '@/lib/hook/useEntity'
// Verrou « le softphone est branché dans cet onglet » : module sans dépendance (aucun
// cycle d'imports réintroduit ici).
import { getSoftphoneLatch } from '@/lib/ringover/softphoneLatch'
import { allRoutesApplication } from '@/lib/routes/CONSTANT_ROUTES'
import getRouteDefault from '@/lib/routes/getRouteDefault'
import { resolveStartPageRoute } from '@/lib/routes/getStartPageRoute'
import { entityDefault, EntityList } from '@/types/Entity'
import pushRoute from '../pushRoute'
import { CareActor, LOCAL_REDIRECT_AFTER_LOGIN } from './AuthClient'
import { useSession } from './hooks'

// Les primitives d'auth vivent désormais dans ./session (module léger, sans Apollo
// ni CONSTANT_ROUTES). On les ré-exporte ici pour ne pas casser les nombreux imports
// existants `from '@/lib/auth/withAuthent'`.
import {
  authClient,
  getSession,
  logout,
  logoutMobile,
  NAME_TOKEN_ENTITY_SWITH_USER,
  NAME_TOKEN_HREF_SWITH_USER,
  overrideRolesSession,
  signIn,
  signOut,
} from './session'

export {
  authClient,
  getSession,
  logout,
  logoutMobile,
  NAME_TOKEN_ENTITY_SWITH_USER,
  NAME_TOKEN_HREF_SWITH_USER,
  overrideRolesSession,
  signIn,
  signOut,
}

export const dispatchRoleDashboard = async ({ router, entityCurrent, dataMyProfile = null }) => {
  const {
    loggedUserRoles,
    rolesProfile: { isUserAdminBackOffice },
  } = getRolesUserAndSwitchUser()

  if (!loggedUserRoles || loggedUserRoles?.length === 0) {
    await routeToLogin()
  } else {
    if (window.localStorage.getItem(LOCAL_REDIRECT_AFTER_LOGIN)) {
      const requestedUrl = window.localStorage.getItem(LOCAL_REDIRECT_AFTER_LOGIN)
      window.localStorage.removeItem(LOCAL_REDIRECT_AFTER_LOGIN)
      router.replace(requestedUrl)
    } else {
      // Preference « page de demarrage » : appliquee UNIQUEMENT a l'atterrissage intentionnel
      // (login / SSO / home), reconnu par la presence de dataMyProfile. La garde de route
      // (withAuthSync) n'a pas le profil et n'en passe pas -> pas de redirection en boucle.
      // Fallback robuste : si la route n'est plus accessible, resolveStartPageRoute renvoie null
      // et le comportement historique (getRouteDefault) s'applique sans changement.
      const startRoute = dataMyProfile
        ? resolveStartPageRoute(dataMyProfile, loggedUserRoles)
        : null
      if (startRoute?.href) {
        if (!!startRoute.entity && startRoute.entity !== entityCurrent) {
          await setEntity(startRoute.entity)
        }
        if (startRoute.href !== router.pathname) {
          await pushRoute({ href: startRoute.href, as: startRoute.as })
        }
        return
      }

      const newRoute = await getRouteDefault({
        router,
        loggedUserRoles,
        entity:
          getIsUserTeams(loggedUserRoles) && entityCurrent === EntityList.PHARMEDIGROUP
            ? EntityList.PHARMEDISCHOOL
            : entityCurrent,
        isUserAdminBackOffice,
      })

      if (!!newRoute?.entity && newRoute?.entity !== entityCurrent) {
        await setEntity(newRoute.entity)
      }
      if (!!newRoute?.href && newRoute?.href !== router.pathname) {
        await pushRoute({ href: newRoute.href, as: newRoute.as })
      }
    }
  }
}

export const routeToLogin = async (email?: string) => {
  await logoutMobile()
  // email optionnel : pre-remplit le champ du login (ex: retour de « mot de passe oublie »).
  const suffix = email?.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''
  await pushRoute({ href: `/login${suffix}`, as: `/login${suffix}` })
}

export const handleSwitchUser = async ({ switchUser, entity = entityDefault, roles = [] }) => {
  if (roles.length == 0) {
    toast.error("Vous n'avez pas les droits pour accéder à cette page")
    return null
  }
  // Utiliser sessionStorage pour isoler par onglet
  sessionStorage.setItem(NAME_TOKEN_HREF_SWITH_USER, window.location.href)
  sessionStorage.setItem(NAME_TOKEN_ENTITY_SWITH_USER, entity)

  authClient.overrideSession({ switchUser, switchUserRoles: roles })
}

/**
 * Retour admin 100 % client EN COURS dans cet onglet.
 *
 * La garde de route (`withAuthSync` ci-dessous) pousse sa propre destination dès que les
 * rôles ne couvrent plus la page affichée — ce qui est exactement le cas une fois la
 * session redevenue admin sur un écran de pharmacien. Sans ce drapeau, deux navigations
 * partaient en même temps (la sienne via `getRouteDefault`, la nôtre vers la page
 * d'origine), chacune avec son `setEntity`. Pendant le retour, la garde se tait et
 * l'écran affiche le loader : la navigation est déjà en vol.
 */
let adminReturnInFlight = false

export const logoutAdmin = async () => {
  // Lu AVANT de rendre la main. Tant que le softphone est BRANCHÉ dans cet onglet, le
  // rechargement de page est interdit : il recharge l'iframe Ringover, donc ferme la
  // fenêtre du téléphone et rejoue la connexion — appel en cours ou non (retour de recette
  // du 18/09 : la fenêtre était perdue à chaque retour admin hors appel). Aucun softphone
  // monté (mode callback, agent sans Ringover, onglet non éligible) : rien à préserver,
  // on garde le rechargement complet, plus sûr.
  const isSoftphoneMounted = getSoftphoneLatch()

  authClient.overrideSession({ switchUser: null, switchUserRoles: null })

  // Revenir sur la page (BO) d'où l'admin avait impersonné. Rester sur la page courante
  // rejouait toutes les queries montées avec l'identité ADMIN — 500 en boucle sur les
  // pages scopées pharmacie (ex: /frontoffice/store-bag/home → GET dashboardStoreBag).
  const backHref = sessionStorage.getItem(NAME_TOKEN_HREF_SWITH_USER)
  const backEntity = sessionStorage.getItem(NAME_TOKEN_ENTITY_SWITH_USER)

  const forgetBackHref = () => {
    sessionStorage.removeItem(NAME_TOKEN_HREF_SWITH_USER)
    sessionStorage.removeItem(NAME_TOKEN_ENTITY_SWITH_USER)
  }

  // Le cache TanStack porte les données du PHARMACIEN impersonné. La purge est faite ICI,
  // et AVANT toute navigation : les trois appelants la faisaient après `logoutAdmin()`, ce
  // qui était sans effet tant que la page était rechargée, mais viderait le cache de la
  // page d'arrivée dans le retour 100 % client ci-dessous.
  queryClient.clear()

  if (isSoftphoneMounted) {
    // Un rechargement complet recharge l'iframe du softphone : la fenêtre se referme, la
    // web app Ringover (4,4 Mo) redémarre, et une communication en cours serait coupée.
    // Retour 100 % client : la session redevient admin, l'iframe survit, la fenêtre reste
    // exactement dans l'état où l'agent l'avait laissée (ouverte/repliée/positionnée) —
    // l'état vit dans `RingoverProvider`, monté au-dessus des pages dans le `Layout`, et
    // rien ne le démonte tant qu'on ne recharge pas. Le motif du rechargement (queries
    // rejouées sous la mauvaise identité) est couvert par la purge ci-dessus ET par le
    // retour sur la page back-office d'origine plutôt qu'un maintien sur place.
    //
    // `switchHref` / `switchEntity` ne sont oubliés qu'APRÈS la navigation : si la garde
    // de route se réveillait malgré le drapeau, elle viserait EXACTEMENT la même
    // destination que nous — jamais l'accueil admin.
    adminReturnInFlight = true
    try {
      if (backEntity) await setEntity(backEntity as EntityList)
      if (backHref) {
        const target = toRelativeHref(backHref)
        await pushRoute({ href: target, as: target })
      }
      forgetBackHref()
    } catch (error) {
      forgetBackHref()
      // Navigation ANNULÉE (`err.cancelled`) : une autre navigation a pris la main, il y a
      // déjà une destination en vol — recharger par-dessus ferait justement la double
      // navigation qu'on cherche à éviter, et perdrait le softphone pour rien.
      // Tout autre échec (cible disparue) : repli sur le comportement historique, on ne
      // laisse pas l'écran figé sur le loader de la garde.
      if (!(error as { cancelled?: boolean })?.cancelled && backHref) {
        window.location.assign(backHref)
      }
    } finally {
      adminReturnInFlight = false
    }

    return
  }

  forgetBackHref()

  // Aucun softphone monté : rechargement complet, on repart d'un état (cache, entité,
  // thème) 100 % admin.
  if (backHref) window.location.assign(backHref)
}

/**
 * `switchHref` est un `window.location.href` ABSOLU. Le routeur Next attend un chemin :
 * une URL absolue partirait en navigation externe (donc en rechargement, ce que le retour
 * en cours d'appel cherche précisément à éviter).
 */
const toRelativeHref = (href: string): string => {
  try {
    const url = new URL(href, window.location.origin)

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}

/** Stores the Care actor resolved from a PIN verification. */
export const setCareActor = (token: string, actor: CareActor) => {
  authClient.overrideSession({ careActorToken: token, careActor: actor })
}

/** Clears the Care actor (e.g. inactivity timeout or "change user"). The counter account stays logged in. */
export const clearCareActor = () => {
  authClient.overrideSession({ careActorToken: null, careActor: null })
}

export const withAuthSync = (WrappedComponent = null) => {
  const Wrapper = props => {
    const session = useSession()
    const { rolesProfile } = getRolesUserAndSwitchUser()

    const router = useRouter()
    const entityCurrent = useEntity()

    const findRoles: string[] = allRoutesApplication
      .filter(r => r.href == router.pathname)
      .map(item => item.roles)
      .flat()

    // @ts-ignore
    const uniqueRoles: string[] = [...new Set(findRoles)]

    // Auto disconnect and redirect to login if the session is unauthenticated
    useEffect(() => {
      if (session.status === 'unauthenticated') {
        window.localStorage.setItem(LOCAL_REDIRECT_AFTER_LOGIN, window.location.href)
        logout()
      }
    }, [session.status])

    if (!rolesProfile.loggedUserRoles.some(r => uniqueRoles.indexOf(r) >= 0)) {
      // Retour admin sans rechargement : la navigation vers la page d'origine est DÉJÀ en
      // vol (cf. `logoutAdmin`). Redemander une destination ici en enverrait une seconde,
      // calculée sans le `switchHref`, donc potentiellement ailleurs. On affiche le loader
      // et on laisse le retour finir — le drapeau retombe dès que la navigation aboutit.
      if (!adminReturnInFlight) {
        dispatchRoleDashboard({
          router,
          entityCurrent,
        })
      }
      return <MainLoader fullScreen />
    }

    return <WrappedComponent {...props} />
  }

  Wrapper.getInitialProps = async ctx => {
    const token = withAuthSync(ctx)

    const componentProps =
      WrappedComponent.getInitialProps && (await WrappedComponent.getInitialProps(ctx))

    return { ...componentProps, token }
  }

  return Wrapper
}
